import {
  WP
} from '../../utils/Domain/Constants';
import {
  GRID_SAVING,
  GRID_REFRESHING,
  GRID_REFRESHED,
  GridAsyncState,
  GRID_DEFAULT,
  ServerScopeResponse,
  isScopeReady,
  GRID_SAVED,
  GRID_ERROR,
  PersistMessage,
  ScopeStateUnion,
  scopeEmpty,
  scopePending,
  scopeReady,
  scopeBusy,
  scopeFailed,
  isReady,
  modifyScopeData,
  modifyScopeDataWithId,
  ScopeReady,
  getScopeId
} from './Scope.types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ScopeCreateRequest, TopMembers } from 'src/services/Scope.client';
import { AxiosInstance } from 'axios';
import {
  CONTEXT_BUSY,
  CONTEXT_FAILED
} from 'src/state/workingSets/workingSets.types';
import { mapValues, values } from 'lodash';
import { CommandDetail, Commands } from './codecs/Commands';
import { deleteScope, getAvailableScopeMembers } from './Scope.actions';

// TODO fix this now because it breaks with auth0 flow
// TODO replace this with a more legit solution to importing query params to state
export const createInitScopeState = (scopeId: string | null = null): ScopeStateUnion => {
  if (scopeId) {
    return scopePending(scopeId);
  }
  return scopeEmpty(false);
};

type ScopeIdPayload = {
  scopeId: string
}

type ScopeRequestPayload = ServerScopeResponse

type ScopeErrorPayload = ScopeIdPayload & {
  error: string
}

const gridSaving = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    gridAsyncState: GRID_SAVING
  }));
};

const gridRefreshing = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    gridAsyncState: GRID_REFRESHING
  }));
};
const gridRefreshed = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    gridAsyncState: GRID_REFRESHED,
    forceRefreshGrid: false
  }));
};

const requestCreateScopeCase =
  (_state: ScopeStateUnion, _action: PayloadAction<ScopeCreateRequest>): ScopeStateUnion => {
    return scopeEmpty(true);
  };
const requestCreateSmartScopeCase =
  (state: ScopeStateUnion, _action: PayloadAction<ScopeCreateRequest>): ScopeStateUnion => {
    return state;
  };


export const anchorIdsFromMemberTrees = (trees: ScopeReady['mainConfig']['memberTrees']): TopMembers => {
  return mapValues(trees, ((tree, key) => {
    // @ts-ignore
    return tree[0].data.map((member) => member.v.id);
  }));
};
const isMultiScope = (topMembers: TopMembers): boolean => {
  // check if each dimension has exactly 1 member
  // if not, it's a multi-scope
  return !values(topMembers).every((mem) => mem.length === 1);
};
const receivedCreateScopeCase =
  (_state: ScopeStateUnion, action: PayloadAction<ServerScopeResponse>): ScopeStateUnion => {
    if (action.payload.type === 'ScopeReady') {
      const anchorMembers = anchorIdsFromMemberTrees(action.payload.memberTrees);
      return scopeReady(action.payload.id, {
        mainConfig: action.payload,
        currentAnchors: anchorIdsFromMemberTrees(action.payload.memberTrees),
        isMultiScope: isMultiScope(anchorMembers),
        isFetching: false,
        forceRefreshGrid: false,
        // TODO: Some of these things should be removed from the payload
        hasEditableRevision: action.payload.revisions.findIndex(r => r.version === WP) > -1 || false,
        inSeason: action.payload.inSeason,
        initialized: action.payload.initialized,
        gridAsyncState: GRID_DEFAULT,
        pendingWrites: 0,
        hasLocks: false,
        balanceOptions: {},
        seedOptions: {},
        importOptions: {},
        overlayOptions: {},
        commands: [],
        persistErrorStatus: undefined,
        message: undefined
      });
    } else {
      return scopePending(action.payload.id);
    }
  };

const requestScopeCase = (_state: ScopeStateUnion, action: PayloadAction<ScopeIdPayload>): ScopeStateUnion => {
  return scopePending(action.payload.scopeId);
};

type ReceivedScopePayload = ScopeRequestPayload & { hasEditableRevision: boolean, client: AxiosInstance };
// How to get this client out ofhere
const receivedScopeCase = {
  prepare: (
    scope: ServerScopeResponse,
    client: AxiosInstance
  ): { payload: ReceivedScopePayload } => {
    // figure out if there is an editable version in the return
    let hasEditableRevision = false;
    if (isScopeReady(scope)) {
      hasEditableRevision = scope.revisions.findIndex(r => r.version === WP) > -1 || false;
      return {
        payload: {
          client,
          hasEditableRevision,
          ...scope
        }
      };
    }
    // This is extremely weird that we jus tsmear everything across like this
    return {
      payload: {
        hasEditableRevision,
        client,
        ...scope
      }
    };
  },
  reducer: (
    state: ScopeStateUnion,
    action: PayloadAction<ReceivedScopePayload>
  ): ScopeStateUnion => {
    if (action.payload.type === 'ScopeReady') {
      // If the scope is ready then we can construct a body for most things
      const anchorMembers = anchorIdsFromMemberTrees(action.payload.memberTrees);
      const body = {
        hasEditableRevision: action.payload.hasEditableRevision,
        availableMembers: action.payload.members,
        currentAnchors: anchorMembers,
        isMultiScope: isMultiScope(anchorMembers),
        isFetching: false,
        inSeason: action.payload.inSeason,
        mainConfig: action.payload,
        initialized: action.payload.initialized,
        gridAsyncState: GRID_DEFAULT,
        pendingWrites: 0,
        // Probably unnecessary
        scopeReady: action.payload.scopeReady,
        forceRefreshGrid: false,
        hasLocks: false,
        balanceOptions: {},
        seedOptions: {},
        commands: [],
        importOptions: {},
        overlayOptions: {},
        message: undefined,
        persistErrorStatus: undefined
      };
      if (action.payload.status === CONTEXT_BUSY) {
        return scopeBusy(action.payload.id, body);
      } else if (action.payload.status === CONTEXT_FAILED) {
        return scopeFailed(action.payload.id, body);
      } else {
        return scopeReady(action.payload.id, body);
      }
    } else {
      return scopePending(action.payload.id);
    }
  }
};
const receivedScopeCaseDynamic = {
  prepare: (
    scope: ServerScopeResponse,
    client: AxiosInstance
  ): { payload: ReceivedScopePayload } => {
    // figure out if there is an editable version in the return
    let hasEditableRevision = false;
    if (isScopeReady(scope)) {
      hasEditableRevision = scope.revisions.findIndex(r => r.version === WP) > -1 || false;
      return {
        payload: {
          client,
          hasEditableRevision,
          ...scope
        }
      };
    }
    // This is extremely weird that we jus tsmear everything across like this
    return {
      payload: {
        hasEditableRevision,
        client,
        ...scope
      }
    };
  },
  reducer: (
    state: ScopeStateUnion,
    action: PayloadAction<ReceivedScopePayload>
  ): ScopeStateUnion => {
    if (action.payload.type === 'ScopeReady') {
      // If the scope is ready then we can construct a body for most things
      const body = {
        ...state,
        mainConfig: action.payload
      };
      return body;
    } else {
      return scopePending(action.payload.id);
    }
  }
};

const scopeNotFoundCase = (
  _state: ScopeStateUnion,
  action: PayloadAction<{ scopeId: string, error?: any }>
): ScopeStateUnion => {
  // TODO: log error here
  return scopeEmpty(false);
};
const clearScopeCase = (): ScopeStateUnion => {
  return scopeEmpty(false);
};

const updatePersistenceStatusCase =
  (state: ScopeStateUnion, action: PayloadAction<PersistMessage>): ScopeStateUnion => {
    return modifyScopeDataWithId(state, (id, data) => {
      const currentScopeStatus = action.payload[id];
      const newAsyncState = currentScopeStatus ?
        currentScopeStatus.inflight === 0 ?
          (currentScopeStatus.previousError == null ? GRID_SAVED : GRID_ERROR)
          : GRID_SAVING :
        GRID_DEFAULT;
      return {
        ...data,
        persistErrorStatus: currentScopeStatus?.previousError,
        gridAsyncState: newAsyncState,
        pendingWrites: currentScopeStatus.inflight
      };
    });
  };

const receivedSeedCurrentScopeCase = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    initialized: true
  }));
};
const updateGridAsyncStateCase = {
  prepare: (newState: GridAsyncState) => {
    return {
      payload: newState
    };
  },
  reducer: (state: ScopeStateUnion, action: PayloadAction<GridAsyncState>): ScopeStateUnion => {
    return modifyScopeData(state, data => ({
      ...data,
      gridAsyncState: action.payload
    }));
  }
};
const requestScopeLockStateCase = (state: ScopeStateUnion): ScopeStateUnion => {
  return state;
};
const receivedScopeLockStateCase = {
  prepare: (newScopeLockState: boolean) => {
    return {
      payload: newScopeLockState
    };
  },
  reducer: (state: ScopeStateUnion, action: PayloadAction<boolean>) => {
    return {
      ...state,
      hasLocks: action.payload
    };
  }
};

const receivedSetEopCase = (state: ScopeStateUnion): ScopeStateUnion => {
  // TODO: some async complete here
  return state;
};

// this is to force refresh workflows without causing a
// infinite loop
const forceRefreshWorkflowsCase = (state: ScopeStateUnion): ScopeStateUnion => {
  // TODO: loading spinner
  return modifyScopeData(state, data => ({
    ...data,
    seedOptions: {},
    workflows: {},
    balanceOptions: {},
    importOptions: {},
    overlayOptions: {}
  }));
};
const receivedCommandsCase = (
  state: ScopeStateUnion,
  action: PayloadAction<Commands>
): ScopeStateUnion => {
  // TODO: actually do something here
  return modifyScopeData(state, data => ({
    ...data,
    commands: action.payload.commands
  }));
};

const commandsErrorsCase = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    commands: [],
    seedOptions: {},
    workflows: {},
    balanceOptions: {},
    importOption: {}
  }));
};

const receivedEopOptionsCase = (
  state: ScopeStateUnion,
  action: PayloadAction<Record<number, CommandDetail[]>>
): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    balanceOptions: action.payload
  }));
};

const requestRefreshGridCase = (state: ScopeStateUnion): ScopeStateUnion => {
  return modifyScopeData(state, data => ({
    ...data,
    forceRefreshGrid: true,
    gridAsyncState: GRID_REFRESHING // is this right?
  }));
};
const requestAttachCase = (state: ScopeStateUnion): ScopeStateUnion => {
  const newRevisions = [
    {
      hidden: false,
      type: 'SingleVersion' as const,
      version: 'ty-review-approved'
    }, {
      against: 'ty-rp',
      type: 'VarianceVersion' as const,
      varType: 'percentage' as const,
      version: 'ty-review-approved'
    }
  ];
  if (isReady(state)) {
    return {
      ...state,
      mainConfig: {
        ...state.mainConfig,
        revisions: [...state.mainConfig.revisions, ...newRevisions]
      }
    };
  }
  return state;
};

const scopeSliceReducer = createSlice({
  name: 'scope',
  initialState: createInitScopeState(),
  reducers: {
    requestCreateScope: requestCreateScopeCase,
    requestCreateSmartScope: requestCreateSmartScopeCase,
    receivedCreateScope: receivedCreateScopeCase,
    requestScope: requestScopeCase,
    receivedScopeDynamic: receivedScopeCaseDynamic,
    receivedScope: receivedScopeCase,
    scopeNotFound: scopeNotFoundCase,
    clearScope: clearScopeCase,
    receivedSeedCurrentScope: receivedSeedCurrentScopeCase,
    updateGridAsyncState: updateGridAsyncStateCase,
    requestScopeLockState: requestScopeLockStateCase,
    receivedScopeLockState: receivedScopeLockStateCase,
    receivedSetEop: receivedSetEopCase,
    // grid async saving
    requestSeedCurrentScope: gridSaving,
    requestSetEop: gridSaving,
    requestImportVersion: gridSaving,
    requestUndoScope: gridSaving,
    requestRedoScope: gridSaving,
    // grid async refreshing
    receiveUndoScope: gridRefreshing,
    receiveRedoScope: gridRefreshing,
    requestRefreshGrid: requestRefreshGridCase,
    // grid async completed refresh
    receiveRefreshGrid: gridRefreshed,
    updatePersistenceStatus: updatePersistenceStatusCase,
    requestAttach: requestAttachCase,
    // workflow actions
    forceRefreshWorkflows: forceRefreshWorkflowsCase,
    workflowsError: commandsErrorsCase,
    // add private version, async logic handled in scope.epics
    receivedEopOptions: receivedEopOptionsCase
  },
  extraReducers: (builder) => {
    builder.addCase(getAvailableScopeMembers.pending, (state, _action) => {
      return modifyScopeData(state, data => ({
        ...data,
        isFetching: true
      }));
    });
    builder.addCase(getAvailableScopeMembers.fulfilled, (state, _action) => {
      return modifyScopeData(state, data => ({
        ...data,
        isFetching: false
      }));
    });
    builder.addCase(getAvailableScopeMembers.rejected, (state, _action) => {
      return modifyScopeData(state, data => ({
        ...data,
        isFetching: false
      }));
    });
    // #region "fetchCommands"
    // #region "deleteScope"
    builder.addCase(deleteScope.fulfilled, (state, action) => {
      // if the user deletes the current scope, wipe it from state
      const currentScopeId = getScopeId(state);
      const deletedScopeId = action.meta.arg;
      if (deletedScopeId === currentScopeId) {
        return scopeEmpty(false);
      }
    });
    // #endregion
    // these use strings due to a import order runtime error
    // that makes it so that accessing the derived .type fails to compile
    builder.addCase<
      string,
      PayloadAction<Commands>
    >('scope/fetchCommands/fulfilled', (state, action) => {
      const scopeWithNewWorkflows = receivedCommandsCase(state, action);
      return scopeWithNewWorkflows;
    });
    builder.addCase('scope/fetchCommands/rejected', (state) => {
      return modifyScopeData(state, data => ({
        ...data,
        workflows: {}
      }));
    });
  }
});

export const {
  requestCreateScope,
  requestCreateSmartScope,
  receivedCreateScope,
  requestScope,
  receivedScope,
  receivedScopeDynamic,
  scopeNotFound,
  clearScope,
  receivedSeedCurrentScope,
  updateGridAsyncState,
  requestScopeLockState,
  receivedScopeLockState,
  requestSeedCurrentScope,
  requestSetEop,
  receivedSetEop,
  requestImportVersion,
  requestUndoScope,
  requestRedoScope,
  receiveUndoScope,
  receiveRedoScope,
  requestRefreshGrid,
  receiveRefreshGrid,
  updatePersistenceStatus,
  requestAttach,
  forceRefreshWorkflows,
  workflowsError,
  receivedEopOptions
} = scopeSliceReducer.actions;
export const { name: scopeSliceName } = scopeSliceReducer;
export default scopeSliceReducer.reducer;
